'use client';

import { User } from '@/types/user';
import { fetcher } from '@/lib/fetcher';
import { ApiResponse } from '../model';
import { API_STATUS } from '@/contants/api';

const SESSION_MIGRATION_REQUIRED = 'SESSION_MIGRATION_REQUIRED';

export class SessionMigrationRequiredError extends Error {
  constructor() {
    super('A fresh sign-in is required for the current application client.');
    this.name = 'SessionMigrationRequiredError';
  }
}

export function isSessionMigrationRequiredError(
  error: unknown
): error is SessionMigrationRequiredError {
  return error instanceof SessionMigrationRequiredError;
}

// SSO Authorization URL service
export function authorize(
  input: { next?: string; referer?: string; project_id?: string } = {
    next: '/chat',
    project_id: '1',
  }
): void {
  try {
    const param = new URLSearchParams();
    if (input.next) {
      param.set('next', input.next);
    }

    if (input.project_id) {
      param.set('project_id', input.project_id);
    }

    let path = '/api/platform/chat-auth/v1/auth/authorization-url?' + param.toString();

    // Let the browser handle the redirect naturally
    if (typeof window !== 'undefined') {
      window.location.href = path;
    } else {
      throw new Error('Cannot redirect: window object not available');
    }
  } catch (error) {
    console.error('Error in authorize function:', error);
    throw error;
  }
}

export function logout(input: { post_logout_redirect_uri?: string }) {
  try {
    const param = new URLSearchParams();
    if (input.post_logout_redirect_uri) {
      param.set('post_logout_redirect_uri', input.post_logout_redirect_uri);
    }

    let path = `/api/platform/chat-auth/v1/auth/logout?${param.toString()}`;

    // Let the browser handle the redirect naturally
    if (typeof window !== 'undefined') {
      window.location.href = path;
    } else {
      throw new Error('Cannot redirect: window object not available');
    }
  } catch (error) {
    console.error('Error logout:', error);
    throw error;
  }
}

export async function getCurrentUserSession(): Promise<ApiResponse<User>> {
  try {
    return await fetcher<ApiResponse<User>>('/api/platform/chat-auth/v1/auth/userinfo/session', {
      method: 'GET',
    });
  } catch (error) {
    console.error('Error getting current user session:', error);
    throw error;
  }
}

/**
 * Get user info from IdP (OIDC provider) - this will auto-refresh tokens if expired
 * Use this on app load/refresh to ensure tokens are fresh
 *
 * This endpoint:
 * - Calls the OIDC provider's userinfo endpoint for real-time data
 * - Automatically refreshes access token if expired (using refresh token)
 * - Updates session tokens in database
 * - Returns fresh user information
 */
export async function getUserInfoFromIdP(): Promise<ApiResponse<User>> {
  try {
    return await fetcher<ApiResponse<User>>('/api/platform/chat-auth/v1/auth/userinfo/idp', {
      method: 'GET',
    });
  } catch (error) {
    console.error('Error getting user info from IdP:', error);
    throw error;
  }
}

export interface UserEntitlements {
  access_token: unknown;
  screens: string[];
  features: string[];
}

/**
 * Get workspace-scoped UI entitlements from IAM.
 *
 * This endpoint:
 * - Calls the IAM authorization endpoint with SelectFields="screens,features"
 * - Returns entitlements even when IAM's bootstrap decision is FORBIDDEN
 * - Requires workspace_id to determine workspace-scoped permissions
 *
 * @param workspaceId - The workspace ID to check permissions for
 * @param organizationId - Optional organization ID for context
 * @returns API response with screens and features in data[0]
 */
export async function getUserEntitlements(
  workspaceId: string,
  organizationId?: string
): Promise<ApiResponse<UserEntitlements>> {
  try {
    const params = new URLSearchParams();
    params.set('workspace_id', workspaceId);
    if (organizationId) {
      params.set('organization_id', organizationId);
    }

    // Use native fetch instead of fetcher to handle FORBIDDEN status gracefully
    const response = await fetch(`/api/platform/chat-auth/v1/auth/me?${params.toString()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = (await response.json()) as ApiResponse<UserEntitlements> & {
      error_code?: string;
    };

    if (response.status === 401 && data.error_code === SESSION_MIGRATION_REQUIRED) {
      throw new SessionMigrationRequiredError();
    }

    // The endpoint may return FORBIDDEN while still returning selected entitlement fields.
    // The caller deliberately consumes the fields and applies fail-closed UI checks.
    return data;
  } catch (error) {
    if (isSessionMigrationRequiredError(error)) {
      throw error;
    }

    console.error('Error getting user entitlements:', error);
    return {
      success: false,
      status: API_STATUS.ERROR,
      data: [{ access_token: {}, screens: [], features: [] }],
      message: 'Failed to fetch user entitlements',
      timestamp: new Date().toISOString(),
    };
  }
}

/** @deprecated Use getUserEntitlements so feature checks cannot be omitted. */
export async function getUserScreens(workspaceId: string, organizationId?: string) {
  return getUserEntitlements(workspaceId, organizationId);
}
